import { useEffect, useRef } from 'react';
import { useGetRelatedProductsQuery } from '../../slices/productApiSlice';
import ProductCarouselSkeleton from '../ui/ProductCarouselSkeleton';
import Message from '../layout/Message';
import { useSelector } from 'react-redux';

import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import '../../assets/styles/product.carousel.css';
import { Autoplay, Pagination } from 'swiper/modules';
import ProductCard from './ProductCard';

const RelatedProductsCarousel = ({ productId }) => {
    const { userInfo } = useSelector((state) => state.auth);
    const swiperRef = useRef(null);
    const carouselRef = useRef(null);
    const {
        data: products,
        isLoading,
        isFetching,
        error,
    } = useGetRelatedProductsQuery({
        productId,
        priceAssigned: userInfo?.priceAssigned || null,
    });

    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) {
            return undefined;
        }

        const setCarouselActive = (active) => {
            const swiper = swiperRef.current;
            if (!swiper || swiper.destroyed) {
                return;
            }

            swiper.allowTouchMove = active;
            if (!swiper.autoplay) {
                return;
            }

            if (active) {
                swiper.autoplay.start();
                return;
            }

            swiper.autoplay.stop();
        };

        const observer = new IntersectionObserver(([entry]) => {
            setCarouselActive(entry.isIntersecting && entry.intersectionRatio >= 0.45);
        }, { threshold: [0, 0.45, 0.7] });

        observer.observe(carousel);
        return () => observer.disconnect();
    }, [products]);

    return isLoading || isFetching ? (
        <ProductCarouselSkeleton slides={4} />
    ) : error ? (
        <Message variant="danger">
            {error?.data?.message || error?.error || 'Ocurrió un error inesperado'}
        </Message>
    ) : (
        products &&
        products.length > 0 && (
            <div className="related-products-carousel mt-5 improved-related-section">
                <div className="related-header">
                    <h2 className="section-title improved-related-title">Artículos que podrían gustarte</h2>
                    <p className="related-subtitle">¡Descubre más opciones seleccionadas especialmente para ti!</p>
                </div>
                <div ref={carouselRef} className="related-products-track">
                <Swiper
                    modules={[Pagination, Autoplay]}
                    slidesPerView={2.4}
                    spaceBetween={10}
                    pagination={{ clickable: true }}
                    autoplay={{ delay: 6000, disableOnInteraction: false }}
                    loop={products.length > 5}
                    simulateTouch={true}
                    allowTouchMove={false}
                    threshold={12}
                    touchAngle={32}
                    touchStartPreventDefault={false}
                    className="product-swiper"
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                        swiper.allowTouchMove = false;
                        swiper.autoplay?.stop();
                    }}
                    breakpoints={{
                        0: { slidesPerView: 2 },
                        320: { slidesPerView: 2.1 },
                        576: { slidesPerView: 3.2 },
                        768: { slidesPerView: 4.2 },
                        1200: { slidesPerView: 5 },
                    }}
                >
                    {products.map((product) => (
                        <SwiperSlide key={product._id}>
                            <ProductCard product={product} variant="carousel" />
                        </SwiperSlide>
                    ))}
                </Swiper>
                </div>
            </div>
        )
    );
};

export default RelatedProductsCarousel;
